import { Button } from '@monorepo/shared/componentsV2/Button';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { ModalFormInstructions } from '@monorepo/shared/componentsV2/modals/contents/ModalFormInstructions';
import { useToast } from '@monorepo/shared/contexts';
import React, { useCallback } from 'react';
import { Form } from 'react-final-form';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { MoveInspectionDateField } from './MoveInspectionDateModal/MoveInspectionDateField';

type DeleteSectionModalFormValues = {
  groupSlug: string;
};

export type DeleteSectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  activeGroupName: string;
};

export function DeleteSectionModal({
  isOpen,
  onClose,
  activeGroupName,
}: DeleteSectionModalProps) {
  const { success: showSuccess } = useToast();

  const {
    formTemplateHelpers: {
      activeGroupSlug,
      deleteGroup,
      inspectionDateField,
      inspectionDateGroupSlug,
    },
  } = useFormTemplateContext();

  const requiresInspectionDateMove =
    inspectionDateField && activeGroupSlug === inspectionDateGroupSlug;

  const onSubmit = useCallback(
    ({ groupSlug }: DeleteSectionModalFormValues) => {
      if (requiresInspectionDateMove && !groupSlug) return;
      deleteGroup(groupSlug);
      onClose();
      showSuccess(`${activeGroupName} was removed.`);
    },
    [
      deleteGroup,
      requiresInspectionDateMove,
      onClose,
      showSuccess,
      activeGroupName,
    ],
  );

  return (
    <Form<DeleteSectionModalFormValues> onSubmit={onSubmit}>
      {({ handleSubmit }) => (
        <BaseModal
          title="Delete Section"
          open={isOpen}
          onClose={onClose}
          maxWidth="sm"
          buttons={
            <>
              <Button onClick={onClose} type="button" variant="text">
                Keep Section
              </Button>
              <Button
                color="secondary"
                onClick={handleSubmit}
                type="submit"
                variant="contained"
              >
                Delete Section
              </Button>
            </>
          }
        >
          <ErrorBoundary>
            <ModalFormInstructions>
              {requiresInspectionDateMove
                ? 'This section contains the inspection date. ' +
                  'Before deleting this section, you must choose a different section for the inspection date. ' +
                  'All other fields in this section will be deleted.'
                : 'Are you sure you want to delete this section? All fields in this section will be deleted.'}
            </ModalFormInstructions>
            {requiresInspectionDateMove && <MoveInspectionDateField />}
          </ErrorBoundary>
        </BaseModal>
      )}
    </Form>
  );
}
